/**
 * This file contains helper functions for Campaign Managment.
 *
 * The entire purpose of Campaign Management is to determine:
 * 1. Where marketing dollars are going
 * 2. What the payback is from that money
 *
 * That's pretty much it.  In order to determine this, we need to look at ALL of the money going toward the campaign,
 * not just dollars.  Time is money for an attorney, so time spent working on a campaign, whether that is setting it
 * up, creating content, monitoring results, or tweaking the campaign, that is time spent and time = money.
 *
 * Specifically, time * billableRate = money.
 *
 * Return on a campaign is measured in terms of billed and collected funds from a client that was referred to us
 * through a campaign.  Note that a lead may use multiple means to find us, and we need to split the returns over
 * those campaigns.  So if a lead got to us through a Google search and really like our website, then we should
 * split their billed and paid money between those campaigns!  This is where it becomes subjective, though.  If the
 * client followed the Google Ad to our website, just so that they could click on the Call Us button, then did the
 * website really influence them?  Probably not.  In that case, is it worth mentioning the web site?  Totally subjective.
 *
 * Right now we are only looking at total costs in and total income received.  In the future we will look at rolling
 * averages to see if the influence of a campaign is increasing or decreasing over time.
 */

import util from "../../../lib/util";
import MathUtil from "../../../lib/mathUtils";
import store from "../../../store/store";
import FinancialHelper, {BilledAndPaidInvoice} from '../../../Financial/FinancialHelper';
import LeadHelper from '../../Sales/LeadHelper';
import Icons from '../../../ui/IconButton/Icons';
import {campaignTypeIcons} from './Campaigns';
import ChartProps from '../../../Charts/ChartProps';
import * as DateUtil from '../../../lib/dateutils';
import * as dfn from 'date-fns';
import {getUpdatedCost} from "../../../TaskManagement/TaskHelper";
import Campaign, {CampaignRecurringCosts} from "../../../entities/campaign";
import Task from "../../../entities/task";
import Matter from "../../../entities/matter";
import {uuid} from "../../../@types/entity";
import Lead from "../../../entities/lead";
import ArrayMap from "../../../lib/ArrayMap";

export type MatterInfo = {
    matter: Matter;
    bapis: BilledAndPaidInvoice[];
    billed: number;
    collected: number;
}

export type CampaignBilledAndCollected = {
    billed: number,
    collected: number,
    matterInfo: MatterInfo[];
};

/**
 * Get (total) hard costs for a campaign, as a number used for calculations.
 *
 * @param {Campaign} campaign
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @returns {number}
 */
export const getHardCosts = (campaign: Campaign, startDate?: Date, endDate?: Date): number => {
    if (!startDate) startDate = new Date(0);
    if (!endDate) endDate = new Date();

    return util.toTwoDecimals(campaign.hardCosts
        .filter(c => DateUtil.isBetween(DateUtil.parseDate(c.incurredOn), startDate, endDate))
        .reduce((total, current) => total + current.cost, 0));
};

/**
 * Return the campaign related to this campaignId.
 *
 * @param {uuid} campaignId
 * @returns {Campaign}
 */
export function getCampaignFromId(campaignId: uuid): Campaign {
    return store.getState().campaigns.get(campaignId);
}

/**
 * Get labor costs in the given date range.
 *
 * @param {Campaign} campaign
 * @param {Task[]} tasks
 * @param {boolean} formatted
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @return {number}
 */
export const getLaborCosts = (campaign: Campaign, tasks: ArrayMap<Task> | Task[], formatted?: boolean, startDate?: Date, endDate?: Date): string | number => {
    if (!startDate) startDate = new Date(0);
    if (!endDate) endDate = new Date();

    if (!tasks) tasks = store.getState().tasks.getMappingFor('campaignId', campaign.id);

    // Have to get all the tasks, do the divvying up, etc.
    const filteredTasks = tasks
        .filter(t => {
            // Check the dates
            if (!DateUtil.isBetween(t.completionDate, startDate, endDate)) return false;

            // See if this task is related to this campaign
            return t.tieIns.some(ti => ti.module === 'Marketing' && ti.entity === 'Campaign' && ti.id === campaign.id);
        });

    const total = filteredTasks
        .map(task => {
            // If there is more than one campaign attached to this task, figure out the split (how many related campaigns)
            const split = task.tieIns.filter(ti => ti.module === 'Marketing' && ti.entity === 'Campaign').length;

            return getUpdatedCost(task) / (split || 1);
        })
        .reduce((total, current) => total + current, 0);

    return formatted ? util.numberWithCommas(total) : util.toTwoDecimals(total);
}

/** The text Date.toISOString writes, which every browser reads the same way. */
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/**
 * The local calendar day a stored recurring-cost date falls on, or null when it does not parse (OWA-980). Writers before
 * July 2022 stored a period in the year of the save as a month and day with no year ("01/31"); writers from July 2022
 * to April 2025 stored MM/DD/YYYY text. Other text goes through DateUtil.parseDate, not `new Date`, so every browser
 * reads it the same way. ISO text, which the editor has stored since April 2025, takes the faster path: the campaign
 * list totals every row of every campaign each time it sorts by cost.
 *
 * @param {unknown} value
 * @return {Date | null}
 */
function storedDay(value: unknown): Date | null {
    if (typeof value !== 'string' && !(value instanceof Date)) return null;

    const date = (typeof value === 'string' && ISO_TIMESTAMP.test(value)) ? new Date(value) : DateUtil.parseDate(value);
    return (date && dfn.isValid(date)) ? dfn.startOfDay(date) : null;
}

/**
 * A stored recurring-cost amount as a number, or null when the row holds none (OWA-980). Writers before 2025-04-11
 * stored amounts as text, such as "100.00", or "NaN" for a period without one.
 *
 * @param {unknown} value
 * @return {number | null}
 */
function storedAmount(value: unknown): number | null {
    const amount = (typeof value === 'string' && value.trim() !== '') ? Number(value) : value;
    return (typeof amount === 'number' && Number.isFinite(amount)) ? amount : null;
}

/**
 * Whether both dates of a stored recurring-cost row parse, so getRecurringCosts can count it (OWA-980). The editor flags
 * a row that fails, such as one from before July 2022 that holds "02/01" with no year, for the firm to correct.
 *
 * @param {CampaignRecurringCosts} row
 * @return {boolean}
 */
export function isDatedRecurringCost(row: CampaignRecurringCosts): boolean {
    return !!storedDay(row.startDate) && !!storedDay(row.endDate);
}

/**
 * A period length the editor can build periods from and save: a whole number of at least 1 (OWA-980).
 *
 * @param {unknown} periodLength
 * @return {boolean}
 */
export function isValidPeriodLength(periodLength: unknown): boolean {
    return Number.isInteger(periodLength) && (periodLength as number) >= 1;
}

/**
 * Build a campaign's recurring-cost rows, one per period, newest first (OWA-980).
 *
 * A period runs from its first day through the day before the next period starts, both counted; the last period stops
 * at the campaign's end date (today for a campaign with no end). getRecurringCosts reads rows the same way.
 *
 * A stored row keeps its amount, including an explicit $0, when it covers the same period:
 * - it starts on the period's first day and ends on the period's last day, or earlier when it is the latest stored row
 *   (cut at the end date, or at the day it was saved, before the end date moved later); or
 * - written before OWA-980, it starts on the period's last day (that writer moved each start there by mistake) and
 *   ends no later.
 * The end check keeps rows from a different period length or unit out without relying on `order`; a row whose `order`
 * names a different period is not used either. Any other period gets the fixed per-period cost, or $0, as does every
 * period of a campaign that has not been saved yet (`keepStoredCosts` false). A row whose start date does not parse
 * matches no period and is kept as stored, after the periods, so a save never drops an amount the firm entered.
 *
 * @param {Campaign} campaign
 * @param {boolean} keepStoredCosts - false for a campaign that has not been saved yet
 * @return {CampaignRecurringCosts[] | null} null when the campaign is not recurring, the period length is not a whole number
 *   of at least 1, or a date is missing, so the caller keeps the rows it has
 */
export function buildRecurringCosts(campaign: Campaign, keepStoredCosts: boolean): CampaignRecurringCosts[] | null {
    if (!campaign.usesPeriod) return null;

    const startDate = DateUtil.toMoment(DateUtil.parseDate(campaign.startDate));
    const endDate = DateUtil.toMoment(DateUtil.parseDate(campaign.endDate) || DateUtil.today());

    const l = campaign.periodUnit;
    const n = campaign.periodLength;
    const period = (l === 'days') ? 'd' : (l === 'weeks') ? 'w' : (l === 'months') ? 'M' : 'y';

    if (!isValidPeriodLength(n) || !startDate?.isValid() || !endDate?.isValid()) return null;

    const numRawPeriods = endDate.diff(startDate, period, true);

    const numConvertedPeriods = numRawPeriods / n;

    // Every period that starts on or before the end date, which is counted (CXR-15-001): a campaign ending on the first
    // day of a period keeps that one-day period, and a campaign that starts and ends on the same day has one period
    const totalPeriods = Math.max(0, Math.floor(numConvertedPeriods + .00001) + 1);

    // Stored rows with the local calendar days they start and end on, by start day. Rows with no start day are kept.
    type Stored = {row: CampaignRecurringCosts, start: Date | null, end: Date | null};
    const stored: Stored[] = campaign.recurringCosts
        .map(row => ({row, start: storedDay(row.startDate), end: storedDay(row.endDate)}));
    const undated = stored.filter(s => !s.start).map(s => s.row);
    const storedByStartDay = new Map<number, Stored>();
    stored.forEach(s => {
        if (s.start && !storedByStartDay.has(s.start.getTime())) storedByStartDay.set(s.start.getTime(), s);
    });
    const latestStart = Math.max(...stored.filter(s => s.start).map(s => s.start.getTime()));

    const results: CampaignRecurringCosts[] = [];

    for (let i = 0; i < totalPeriods; i++) {
        const start = startDate.clone().add(n * i, period);
        const computedEndDate = startDate.clone().add(n * (i + 1), period).subtract(1, 'd');

        const end = DateUtil.min(computedEndDate, endDate);

        const firstDay = dfn.startOfDay(start.toDate());
        const lastDay = dfn.startOfDay(computedEndDate.toDate());
        const legacyStart = start.clone().add(n, period).subtract(1, 'd');

        const current = storedByStartDay.get(firstDay.getTime());
        const legacy = storedByStartDay.get(dfn.startOfDay(legacyStart.toDate()).getTime());
        const coversPeriod = current && (
            !current.end
            || current.end.getTime() === lastDay.getTime()
            || (current.start.getTime() === latestStart && current.end >= current.start && current.end < lastDay)
        );
        const coversLegacyPeriod = legacy && (!legacy.end || legacy.end <= legacy.start);

        const match = [coversPeriod ? current : undefined, coversLegacyPeriod ? legacy : undefined]
            .find(s => s && (typeof s.row.order !== 'number' || s.row.order === i));
        const storedCost = match ? storedAmount(match.row.cost) : null;

        const cost = (keepStoredCosts && storedCost !== null)
                     ? storedCost
                     : campaign.usesPeriodFixedCost
                       ? campaign.periodFixedCost
                       : 0;

        results.push({
            order: i,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            cost: util.toTwoDecimals(cost)
        });
    }

    results.sort((a, b) => b.order - a.order);

    return [...results, ...undated];
}

/**
 * Get recurring costs for a campaign.
 *
 * Periods and the date range are counted in whole local calendar days, first and last day included (OWA-980), so a
 * period that starts and ends on the same day is one day, and a one-day range takes one day of a period's cost. A
 * period that overlaps the range only in part contributes its cost in proportion to the days that overlap. A campaign
 * that is not recurring has no recurring costs, whatever rows it still holds, and a row whose dates or amount do not
 * parse counts nothing.
 *
 * @param {Campaign} campaign
 * @param {boolean} formatted
 * @param {Date} [startDate] - no lower bound when omitted
 * @param {Date} [endDate] - today when omitted, like the hard and labor costs
 * @return {number | string}
 */
export const getRecurringCosts = (campaign: Campaign, formatted?: boolean, startDate?: Date, endDate?: Date): number | string => {
    const rangeStart = startDate ? dfn.startOfDay(startDate) : null;
    const rangeEnd = dfn.startOfDay(endDate ?? new Date());

    const total = !campaign.usesPeriod ? 0 : campaign.recurringCosts
        .reduce((total, current) => {
            const cStart = storedDay(current.startDate);
            const cEnd = storedDay(current.endDate);
            const cost = storedAmount(current.cost);
            if (!cStart || !cEnd || cost === null) return total;

            const totalDays = dfn.differenceInCalendarDays(cEnd, cStart) + 1;
            if (totalDays < 1) return total; // An end before its start covers no days

            const overlapStart = (rangeStart && rangeStart > cStart) ? rangeStart : cStart;
            const overlapEnd = rangeEnd < cEnd ? rangeEnd : cEnd;
            const overlapDays = dfn.differenceInCalendarDays(overlapEnd, overlapStart) + 1;
            if (overlapDays < 1) return total;

            return total + (cost * overlapDays / totalDays);
        }, 0);

    return formatted ? util.numberWithCommas(total) : util.toTwoDecimals(total);
};

/**
 * Get all costs, as a string
 *
 * @param {Campaign} campaign - the campaign to total
 * @param {Task[]} [tasks]
 * @param {boolean} [formatted] - format with $ and commas?
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @returns {number|string}
 */
export const getTotalCost = (campaign: Campaign, tasks?: Task[], formatted?: boolean, startDate?: Date, endDate?: Date): number | string => {
    if (!tasks) tasks = store.getState().tasks.getMappingFor('campaignId', campaign.id);

    const total = (getHardCosts(campaign, startDate, endDate) as number)
        + (getLaborCosts(campaign, tasks, false, startDate, endDate) as number)
        + (getRecurringCosts(campaign, false, startDate, endDate) as number);

    return formatted ? util.numberWithCommas(total) : util.toTwoDecimals(total);
};

export const getIcon = (campaign: Campaign) => {
    let icon = campaignTypeIcons.get(campaign.category);
    if (/facebook/i.test(campaign.name)) icon = Icons.FACEBOOK;
    else if (/google/i.test(campaign.name)) icon = Icons.GOOGLE;
    else if (/twitter/i.test(campaign.name)) icon = Icons.TWITTER;
    else if (/yelp/i.test(campaign.name)) icon = Icons.YELP;

    return icon;
};

/**
 * Get the components of the ROI for a campaign, given optional date ranges. Note that the returns are how much the
 * campaign INFLUENCED, not a direct return, as there can be more than 1 influencer for a lead.
 *
 * Dates are used to determine the spend (campaign costs) and returns (invoices) used in the calculation.
 *
 * When there are multiple campaigns and/or referrals for a matter, we need to proportion them out.
 *
 * So if we have a lead with two campaigns and a referral, e.g. website, Google and Bob, the credit will be allocated
 * as such:
 * - Bob get 50%
 * - Google gets 25%
 * - Website gets 25%
 *
 * Methodology:  Bob = 2, Google = 1, Website = 1: total 4, thus 100%/4 = 25% per unit.
 *
 * @param {Campaign} campaign
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @return {CampaignBilledAndCollected}
 */
export const getCampaignBilledAndCollected = (campaign: Campaign, startDate?: Date, endDate?: Date): CampaignBilledAndCollected => {
    if (!startDate) startDate = new Date(0);
    if (!endDate) endDate = new Date();

    // Keep track of the total influenced dollars, both billed and collected
    let billed = 0;
    let collected = 0;

    /**
     * Get all leads associated with a campaign
     */
    const leads = store.getState().leads.getMappingFor('campaignId', campaign.id);

    /**
     * Store matters for these leads.
     */
    const matterInfo: Array<MatterInfo> = [];

    /* For each lead, get the related campaigns and referrers */
    leads.forEach(lead => {
        /**
         * Get all matters associated with this lead
         */
        const matters = store.getState().matters.getMappingFor('leadId', lead.id);

        // For each matter, get the influenced amount.
        matters.forEach(matter => {
            /**
             * Invoices for this matter, if any.
             */
            let matterInvoices = FinancialHelper.getInvoices(matter.id, 'asc');

            // Get the first invoice for the matter by looking up all the invoices, taking the first one,
            // and getting the completion date of the first task (if any).
            const invoiceTasks = matterInvoices[0]?.taskIds
                .map(t => store.getState().tasks.get(t))
                .sort((a, b) => (a.completionDate?.getTime() ?? 0) - (b.completionDate?.getTime() ?? 0)) ?? [];

            const matterCreated = invoiceTasks.filter(util.existsFilter).length > 0 && invoiceTasks[0].completionDate
                                  ? invoiceTasks[0].completionDate.getTime() - 1
                                  : 0;

            // If there are no invoices and no startingBilled or startingCollected, just return.
            // Otherwise, calculate relevant bapis.
            if (matterCreated || matter.startingBilled || matter.startingCollected) {
                let _billed = 0;
                let _collected = 0;

                // Figure out if we need to include startingBilled and collected
                const isBetween = matterCreated > 0 ? dfn.isBefore(matterCreated, endDate)
                                                    : DateUtil.isBetween(matterCreated, startDate, endDate);
                if (isBetween) {
                    _billed = matter.startingBilled;
                    _collected = matter.startingCollected;
                }

                // For this specific matter, calculate the amounts billed and collected.
                const bapis = FinancialHelper.getBilledAndPaidForMatters([matter.id]);

                bapis.filter(bapi => DateUtil.isBetween(bapi.endDate, startDate, endDate))
                    .forEach((bapi) => {
                        _billed += bapi.billed;
                        _collected += bapi.collected;
                        billed += bapi.billed;
                        collected += bapi.collected;
                    });

                matterInfo.push({matter, bapis, billed: _billed, collected: _collected});
            }
        });
    });

    return {
        billed: util.toTwoDecimals(billed),
        collected: util.toTwoDecimals(collected),
        matterInfo
    };
};


/**
 * Get the components of the ROI.
 *
 * When there are multiple campaigns and/or referrals for a matter, we need to proportion them out.
 * For now we will assume that a referral is much more powerful than a campaign, and will garner [referralWeight] the credit.
 *
 * So if we have a lead with two campaigns and a referral, e.g. website, Google and Bob, the credit will be allocated
 * as such:
 * - Bob get 50%
 * - Google gets 25%
 * - Website gets 25%
 *
 * Methodology:  Bob = 2, Google = 1, Website = 1: total 4, thus 100%/4 = 25% per unit.
 *
 * @param {number} referralWeight
 * @return {{billed: number, collected: number}}
 */
export const getReferralRoi = (referralWeight: number = 1): CampaignBilledAndCollected => {
    // Keep track of the total influenced dollars, both billed and collected
    let billed = 0;
    let collected = 0;

    const matterInfo = [];

    /**
     * Get all leads associated with referrals
     */
    const leads = store.getState().leads.filter(lead => lead.referrers.length > 0);

    leads.forEach(lead => {
        const campaigns = lead.campaigns.length;
        const referrals = lead.referrers.length;
        const influence = (referrals * referralWeight) / (campaigns + (referrals * referralWeight));

        const matters = store.getState().matters.getMappingFor('leadId', lead.id);

        // For each matter, get the influenced amount based on the campaign's lead influence.
        matters.forEach(matter => {
            let thisBilled = matter.startingBilled;
            let thisCollected = matter.startingCollected;
            billed += thisBilled;
            collected += thisCollected;

            // For this specific matter, calculate the amounts billed and collected.
            const bapis = FinancialHelper.getBilledAndPaidForMatters([matter.id]);

            bapis.forEach((bapi) => {
                thisBilled += bapi.billed;
                thisCollected += bapi.collected;
                billed += bapi.billed;
                collected += bapi.collected;
            });

            matterInfo.push({matter, bapis, billed: thisBilled / influence, collected: thisCollected / influence});
        });
    });

    return {
        billed: util.toTwoDecimals(billed),
        collected: util.toTwoDecimals(collected),
        matterInfo
    };
};

/**
 * Get cases for a campaign
 *
 * @param {Campaign} campaign
 */
export const getCases = (campaign: Campaign) => {
    return store.getState().matters.getMappingsFor('leadId', getLeadIds(campaign)); // Matters with leads
};

/**
 * Get lead Ids for a campaign
 *
 * @param {Campaign} campaign
 */
export function getLeadIds(campaign: Campaign) {
    return getLeads(campaign).map(l => l.id);
}

/**
 * get Leads for a campaign
 * @param {Campaign} campaign
 */
export const getLeads = (campaign: Campaign) => {
    return util.distinctId<Lead>(store.getState().leads
        .filter(l => l?.campaigns.includes(campaign.id))
        .filter(util.existsFilter));
};

/**
 * Get the average cost per lead for this campaign.  This is the cost to get a person to contact the firm, so
 * any lead will do.  Take the total cost of the campaign, so far, and divide by the number of leads.
 *
 * If no campaign is passed we get the overall (average) cost per lead.
 *
 * @param {Campaign} [campaign]
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @returns {number}
 */
export const getCostPerLead = (campaign?: Campaign, startDate?: Date, endDate?: Date): number => {
    if (campaign) {
        const totalCost = getTotalCost(campaign, null, false, startDate, endDate) as number;

        const allLeads = store.getState().leads
            .getMappingFor('campaignId', campaign.id)
            .map(l => l.id)
            .filter(util.existsFilter)
            .filter(util.distinctFilter);

        if (allLeads.length === 0) return totalCost;

        return util.fixed(totalCost / (allLeads.length || 1), 2);
    }
    else {
        // Get the global average
        const totalLeads = store.getState().leads.filter(util.deletedFilter).length;

        const totalCost = MathUtil.sum(store.getState().campaigns
            .filter(util.deletedFilter)
            .map(c => getTotalCost(c) as number));

        if (totalCost === 0) return 0;
        else return util.fixed(totalCost / totalLeads, 2);
    }
};

/**
 * Get the conversion rate for leads from this campaign.  This is the number of leads with this campaign as a
 * contributing factor that lead to a won case (meaning a case has been created from the lead).
 *
 * Note that conversion rate is defined as
 * If no campaign is passed, we get global average conversion rate.
 *
 * @param {Campaign} [campaign]
 * @returns {number}
 */
export const getConversionRate = (campaign?: Campaign): number => {
    let convertedLeads: string | any[];
    let qualifiedLeads: string | any[];

    if (campaign) {
        convertedLeads = util.distinctId<Lead>(store.getState().matters
            .getIndex('hasLead')
            .map(m => LeadHelper.getLeadFromId(m.leadId)) // Lead objects
            .filter(util.existsFilter)
            .filter(l => LeadHelper.isQualified(l) && l?.campaigns.includes(campaign.id))
            .filter(util.existsFilter));

        qualifiedLeads = util.distinctId<Lead>(store.getState().leads
            .getMappingFor('campaignId', campaign.id)
            // .filter(l => l?.campaigns.includes(campaign.id))
            .filter(util.existsFilter)
            .filter(LeadHelper.isQualified));
    }
    else {
        convertedLeads = store.getState().matters
            .map(m => LeadHelper.getLeadFromId(m.leadId))
            .filter(util.existsFilter)
            .filter(LeadHelper.isQualified);

        qualifiedLeads = store.getState().leads
            .filter(LeadHelper.isQualified);
    }

    if (qualifiedLeads.length === 0) return 0;
    else return util.fixed(100 * convertedLeads.length / qualifiedLeads.length, 0);
};

/**
 * Get the ROI calculations for campaigns, given the date constraints.
 *
 * Date ranges are interpreted as follows:
 * - Spend will be based on money spent between the date ranges given. This is taken from campaigns.
 * - Returns will be based on money *collected* during the given period. This is taken from invoices.
 *
 * @param {Campaign[]} campaigns
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 */
export const getRoiChartData = (campaigns: Campaign[] | ArrayMap<Campaign>, startDate?: Date, endDate?: Date): RoiChartDataResult[] => {
    const colors = [...ChartProps.generateColors(.8, .5), ...ChartProps.generateColors(.6, .4)];

    return campaigns.map((c: Campaign, i: number) => {
        const roiBC = getCampaignBilledAndCollected(c, startDate, endDate);
        const totalCost = getTotalCost(c, null, false, startDate, endDate) as number;
        let roi = (roiBC.collected || 0) / (totalCost || 1);
        if (roi > 20) roi = 20;
        return {
            id: c.name,
            data: [
                {x: 'Spend', y: 1 + roi / 100}, // add a fraction of the roi so that we can sort these from best to worst ROI
                {x: 'Returns', y: roi} //
                // {x: 'Spend', y: getTotalCost(c, tasks, false, startDate, endDate)},
                // {x: 'Returns', y: roi?.collected || 0}
            ],
            spent: totalCost,
            received: roiBC.collected,
            roi: roiBC,
            color: colors[i]
        };
    })
        .filter(o => o.spent > 0 || o.received > 0);
}

export type RoiChartDataResult = {
    id: string;
    data: {
        x: string;
        y: number;
    }[];
    spent: number;
    received: number;
    roi: {
        billed: number;
        collected: number;
    };
    color: string;
}